// Guest identity: a random secret token (never shown to others) and a display name, in localStorage.
// `?player=2` gives this tab a separate identity (kept in sessionStorage), so one person can test
// multiplayer with several tabs of the same browser.
function tabIdentity(): string {
  try {
    const q = new URLSearchParams(location.search).get('player');
    if (q && /^[a-z0-9]{1,8}$/i.test(q)) sessionStorage.setItem('bcm.as', q);
    const as = sessionStorage.getItem('bcm.as');
    return as ? `.${as}` : '';
  } catch {
    return '';
  }
}
const SUFFIX = tabIdentity();
const TOKEN_KEY = `bcm.token${SUFFIX}`;
const NAME_KEY = `bcm.name${SUFFIX}`;

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: identity lasts for this tab only */
  }
}

let memoryToken: string | null = null;

export function playerToken(): string {
  const existing = safeGet(TOKEN_KEY) ?? memoryToken;
  if (existing) return existing;
  const token = [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, '0')).join('');
  memoryToken = token;
  safeSet(TOKEN_KEY, token);
  return token;
}

const NAMES = ['Clerk Ambrose', 'Notary Quince', 'Scribe Wren', 'Inkwright Tobias', 'Filer Maud', 'Stamper Odile'];

export function playerName(): string {
  return safeGet(NAME_KEY) ?? NAMES[Math.floor(Math.random() * NAMES.length)]!;
}

export function setPlayerName(name: string): void {
  safeSet(NAME_KEY, name.trim().slice(0, 24));
}

export function prefs<T>(key: string, fallback: T): T {
  const raw = safeGet(`bcm.pref.${key}`);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
export function setPrefs(key: string, value: unknown): void {
  safeSet(`bcm.pref.${key}`, JSON.stringify(value));
}

/** The match this tab was last in, so relaunching the app (or a reload) goes straight back to it. */
export type LastSession = { mode: 'offline' } | { mode: 'online'; code: string };
const SESSION_KEY = `bcm.session${SUFFIX}`;

export function lastSession(): LastSession | null {
  try {
    const raw = safeGet(SESSION_KEY);
    return raw ? (JSON.parse(raw) as LastSession) : null;
  } catch {
    return null;
  }
}
export function setLastSession(s: LastSession | null): void {
  if (s) safeSet(SESSION_KEY, JSON.stringify(s));
  else {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      /* storage unavailable */
    }
  }
}
